import { useEffect, useState, useMemo } from 'react';
import {
    auth, db, linkWithPopup, onAuthStateChanged, createUserWithEmailAndPassword,
    signInWithEmailAndPassword, signOut, signInWithPopup, getAdditionalUserInfo,
    sendPasswordResetEmail, GoogleAuthProvider, reauthenticateWithPopup, doc, setDoc, getDoc, serverTimestamp, deleteDoc, appId, writeBatch
} from '../api/firebase.js';
import { trackEvent, setUserProperties } from '../api/firebase.js';
import { smtp2goService } from '../api/smtp2go.js';
import { trackAccountCreated, setUserAnalyticsProperties } from '../utils/userAnalytics.js';
import { trackTrialStarted, trackSubscriptionPurchased } from '../utils/businessAnalytics.js';
import logger from '../utils/logger';

// The new file no longer needs to declare 'appId' locally.
// It will be accessed directly from the imported Firebase file.

/**
 * DEFENSE LAYER 1: Retry Logic with Exponential Backoff
 * Prevents data loss from temporary network failures
 */
async function firestoreWriteWithRetry(writeOperation, operationName = 'write', maxRetries = 3) {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
            await writeOperation();
            console.log(`\u2705 Firestore ${operationName} succeeded (attempt ${attempt}/${maxRetries})`);
            return { success: true };
        } catch (error) {
            logger.error('App', `\u274C Firestore ${operationName} failed (attempt ${attempt}/${maxRetries}):`, error);
            
            if (attempt === maxRetries) {
                // Final attempt failed - log for recovery
                logger.error('App', `\u{1F6A8} ALL RETRY ATTEMPTS FAILED for ${operationName} - User may be orphaned`);
                // Store in localStorage for client-side recovery
                localStorage.setItem('failedUserCreation', JSON.stringify({
                    timestamp: Date.now(),
                    error: error.message,
                    operation: operationName,
                    userId: auth.currentUser?.uid
                }));
                throw error;
            }
            
            // Exponential backoff: 1s, 2s, 4s
            const delay = Math.pow(2, attempt - 1) * 1000;
            console.log(`\u23F3 Retrying ${operationName} in ${delay}ms...`);
            await new Promise(resolve => setTimeout(resolve, delay));
        }
    }
}

/**
 * ENHANCED: Transaction Coordinator for User Creation
 * Creates both parent and progress documents atomically using batch writes
 * Includes compensating transaction to delete auth if Firestore fails
 */
async function createUserWithTransaction(firebaseUser, userData, isSignup = true) {
    const uid = firebaseUser.uid;
    const transactionId = `txn_${uid}_${Date.now()}`;
    
    logger.info('createUserWithTransaction', 'Info');
    
    try {
        // Phase 1: Create Firestore documents atomically with batch
        const batch = writeBatch(db);
        
        // Parent user document (for collection queries)
        const userDocRef = doc(db, 'artifacts', appId, 'users', uid);
        batch.set(userDocRef, {
            uid,
            email: userData.email || userData.accountEmail,
            displayName: userData.accountName || 'User',
            createdAt: serverTimestamp(),
            lastUpdated: serverTimestamp(),
            transactionId, // Track transaction for audit
            creationMethod: isSignup ? 'direct_signup' : 'healed'
        }, { merge: true });
        
        // Progress document (main user data)
        const progressDocRef = doc(db, 'artifacts', appId, 'users', uid, 'progress', 'user_data');
        batch.set(progressDocRef, {
            ...userData,
            transactionId,
            createdAt: serverTimestamp(),
            lastUpdated: serverTimestamp()
        }, { merge: true });
        
        // Commit atomically (both succeed or both fail)
        await batch.commit();
        
        logger.info('createUserWithTransaction', 'Info');
        return { success: true, transactionId, uid };
        
    } catch (error) {
        logger.error('createUserWithTransaction', 'Error', error);
        
        // COMPENSATING TRANSACTION: Delete auth if Firestore creation failed
        // This prevents orphaned auth accounts
        if (isSignup) {
            logger.warn('createUserWithTransaction', 'Warning');
            logger.warn('createUserWithTransaction', 'Warning');
            
            try {
                await firebaseUser.delete();
                logger.info('createUserWithTransaction', 'Info');
                return { 
                    success: false, 
                    compensated: true, 
                    error: error.message,
                    userMessage: 'Account creation failed. Please try again.'
                };
            } catch (deleteError) {
                logger.error('createUserWithTransaction', 'Error', deleteError);
                logger.error('createUserWithTransaction', 'Error', uid);
                
                // Log to external monitoring (if available)
                try {
                    trackEvent('orphaned_auth_created', {
                        uid,
                        transactionId,
                        originalError: error.message,
                        deleteError: deleteError.message
                    });
                } catch (e) {
                    // Ignore analytics errors
                }
                
                return { 
                    success: false, 
                    compensated: false,
                    orphaned: true,
                    error: error.message,
                    deleteError: deleteError.message,
                    userMessage: 'Account creation incomplete. Please contact support.'
                };
            }
        } else {
            // Not a signup (healing attempt) - don't delete auth
            return { 
                success: false, 
                error: error.message,
                userMessage: 'Failed to recover account data.'
            };
        }
    }
}

const useAuth = () => {
    // --- REAL FIREBASE AUTHENTICATION ---
    const [user, setUser] = useState(undefined);
    const [isLoading, setIsLoading] = useState(true);

    /**
     * Helper function to create parent user document
     * DEPRECATED: Use createUserWithTransaction instead for new signups
     * Kept for backward compatibility with healing function
     */
    const createUserDocument = async (userId, email, displayName = null) => {
        try {
            const userDocRef = doc(db, 'artifacts', appId, 'users', userId);
            await firestoreWriteWithRetry(async () => {
                await setDoc(userDocRef, {
                    uid: userId,
                    email: email,
                    displayName: displayName || 'User',
                    createdAt: serverTimestamp(),
                    lastUpdated: serverTimestamp()
                }, { merge: true });
            }, `createParentDoc[${userId}]`);
            logger.info('useAuth', 'Info', userId);
        } catch (error) {
            logger.error('useAuth', 'Error', error);
            // Don't throw - this is not critical for app functionality
        }
    };

    /**
     * DEFENSE LAYER 3: Auto-Healing for Orphaned Users
     * Attempts to recover orphaned users by creating minimal valid data
     */
    const healOrphanedUser = async (firebaseUser) => {
        console.log('\u{1F527} Attempting to heal orphaned user:', firebaseUser.uid);
        
        // NEW: Extra check - maybe onboarding just completed
        const justCompleted = localStorage.getItem('justCompletedOnboarding');
        if (justCompleted) {
            const timeSince = Date.now() - parseInt(justCompleted);
            if (timeSince < 60000) {
                console.log('\u23ED\uFE0F Healing skipped - onboarding completed recently');
                return { success: true, method: 'skipped_recent_onboarding' };
            }
        }
        
        const onboardingInProgress = localStorage.getItem('onboardingOperationId');
        if (onboardingInProgress) {
            console.log('\u23ED\uFE0F Healing skipped - onboarding transaction in progress');
            return { success: true, method: 'skipped_onboarding_in_progress' };
        }
        
        try {
            // Check if there's temp profile data from interrupted sign-up
            const tempProfileData = localStorage.getItem('tempProfileData');
            
            if (tempProfileData) {
                console.log('\u2705 Found temp profile data - completing onboarding');
                const profileData = JSON.parse(tempProfileData);
                
                // Create parent document
                await createUserDocument(firebaseUser.uid, firebaseUser.email, firebaseUser.displayName);
                
                // Create minimal progress data (onboarding will complete it)
                const docRef = doc(db, 'artifacts', appId, 'users', firebaseUser.uid, 'progress', 'user_data');
                await firestoreWriteWithRetry(async () => {
                    await setDoc(docRef, {
                        email: firebaseUser.email || null,
                        accountEmail: firebaseUser.email || null,
                        accountName: firebaseUser.displayName || 'User',
                        onboardingComplete: false, // Force them back to onboarding
                        profiles: {},
                        lastActiveProfileId: null,
                        subscriptionStatus: 'trial',
                        trialStartDate: serverTimestamp(),
                        trialEndDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
                        emailPreferences: {
                            promotions: true,
                            reports: 'weekly'
                        },
                        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
                        emailCampaigns: {
                            welcomeSent: false,
                            midTrialSent: false,
                            trialExpirySent: false
                        },
                        healedAt: serverTimestamp(), // Mark as auto-healed
                        healedReason: 'orphaned_user_recovery_with_temp_profile'
                    }, { merge: true });
                }, `healOrphan[${firebaseUser.uid}]`);
                
                localStorage.removeItem('tempProfileData');
                console.log('\u2705 Orphaned user healed successfully with temp profile');
                return { success: true, method: 'temp_profile' };
            }
            
            // No temp data - create minimal valid user data
            console.log('\u26A0\uFE0F No temp profile data - creating minimal user data');
            
            // Create parent document
            await createUserDocument(firebaseUser.uid, firebaseUser.email, firebaseUser.displayName);
            
            const docRef = doc(db, 'artifacts', appId, 'users', firebaseUser.uid, 'progress', 'user_data');
            await firestoreWriteWithRetry(async () => {
                await setDoc(docRef, {
                    email: firebaseUser.email || null,
                    accountEmail: firebaseUser.email || null,
                    accountName: firebaseUser.displayName || 'User',
                    onboardingComplete: false, // Force them back to onboarding
                    profiles: {},
                    lastActiveProfileId: null,
                    subscriptionStatus: 'trial',
                    trialStartDate: serverTimestamp(),
                    trialEndDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
                    emailPreferences: {
                        promotions: true,
                        reports: 'weekly'
                    },
                    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
                    emailCampaigns: {
                        welcomeSent: false,
                        midTrialSent: false,
                        trialExpirySent: false
                    },
                    healedAt: serverTimestamp(), // Mark as auto-healed
                    healedReason: 'orphaned_user_recovery_minimal'
                }, { merge: true });
            }, `healOrphan[${firebaseUser.uid}]`);
            
            console.log('\u2705 Minimal user data created - user redirected to onboarding');
            return { success: true, method: 'minimal_data' };
            
        } catch (error) {
            logger.error('App', '\u274C Failed to heal orphaned user:', error);
            return { success: false, method: 'failed', error: error.message };
        }
    };

    /**
     * DEFENSE LAYER 3: Backfill Missing Fields
     * Repairs incomplete user data
     */
    const backfillMissingFields = async (firebaseUser, userData, missingFields) => {
        console.log('\u{1F527} Backfilling missing fields:', missingFields);
        
        try {
            const updates = {};
            
            if (missingFields.includes('accountEmail') && firebaseUser.email) {
                updates.accountEmail = firebaseUser.email;
                if (!userData.email) {
                    updates.email = firebaseUser.email;
                }
            }
            if (missingFields.includes('subscriptionStatus')) {
                updates.subscriptionStatus = 'trial';
            }
            if (missingFields.includes('trialStartDate')) {
                updates.trialStartDate = serverTimestamp();
                updates.trialEndDate = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();
            }
            
            if (Object.keys(updates).length > 0) {
                const docRef = doc(db, 'artifacts', appId, 'users', firebaseUser.uid, 'progress', 'user_data');
                await firestoreWriteWithRetry(async () => {
                    await setDoc(docRef, {
                        ...updates,
                        backfilledAt: serverTimestamp(),
                        backfilledFields: missingFields
                    }, { merge: true });
                }, `backfill[${firebaseUser.uid}]`);
                
                console.log('\u2705 Successfully backfilled fields:', Object.keys(updates));
            }
        } catch (error) {
            logger.error('App', '\u274C Failed to backfill fields:', error);
        }
    };

    useEffect(() => {

        // Check if we should use mock authentication
        // Only use mock auth if explicitly set OR on localhost without real auth enabled
        const useRealAuth = localStorage.getItem('speak-roots-use-real-auth') === 'true';
        const useMockAuth = (window.location.hostname === 'localhost' && !useRealAuth) || 
                           localStorage.getItem('speak-roots-use-mock-auth') === 'true';
        
        if (useMockAuth) {
            console.log('Using mock authentication for:', window.location.hostname);
            const mockUser = {
                uid: 'prod-user-123',
                isAnonymous: false,
                email: 'user@kinsthread.com',
                displayName: 'Production User'
            };
            setUser(mockUser);
            setIsLoading(false);
            return;
        }
        
        // For Netlify deployments, use real Firebase authentication
        console.log('Using real Firebase authentication for:', window.location.hostname);
        
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            console.log('Auth state changed:', currentUser ? { uid: currentUser.uid, isAnonymous: currentUser.isAnonymous, email: currentUser.email } : null);
            
            // DEFENSE LAYER 2: Auth State Reconciliation
            // Check if user data exists in Firestore and heal if needed
            if (currentUser) {
                try {
                    // Check if onboarding was just completed (within last 60 seconds)
                    const justCompletedOnboarding = localStorage.getItem('justCompletedOnboarding');
                    if (justCompletedOnboarding) {
                        const completedTime = parseInt(justCompletedOnboarding);
                        const timeSinceCompletion = Date.now() - completedTime;
                        // EXTENDED: 10s -> 60s to cover all auth state changes and Firestore propagation
                        if (timeSinceCompletion < 60000) {  // 60 seconds (was 10000)
                            console.log('\u23ED\uFE0F Skipping orphan check - onboarding just completed');
                            console.log(`   Time since completion: ${Math.round(timeSinceCompletion / 1000)}s`);
                            setUser(currentUser);
                            setIsLoading(false);
                            return;
                        } else {
                            // Clean up old flag
                            console.log('\u{1F9F9} Cleaning up old justCompletedOnboarding flag');
                            localStorage.removeItem('justCompletedOnboarding');
                        }
                    }
                    
                    const userDocRef = doc(db, 'artifacts', appId, 'users', currentUser.uid, 'progress', 'user_data');
                    const userDoc = await getDoc(userDocRef);
                    
                    if (!userDoc.exists()) {
                        console.warn('\u{1F6A8} ORPHANED USER DETECTED:', currentUser.uid);
                        console.warn('   Firebase Auth exists but no Firestore data');
                        
                        // NEW: Check if onboarding is actively in progress
                        const onboardingInProgress = localStorage.getItem('onboardingOperationId');
                        if (onboardingInProgress) {
                            console.log('\u23F3 Onboarding transaction in progress - delaying orphan check');
                            console.log('   Operation ID:', onboardingInProgress);
                            
                            // Wait 5 seconds for transaction to complete
                            await new Promise(resolve => setTimeout(resolve, 5000));
                            
                            // Re-check if document exists now
                            const recheckDoc = await getDoc(userDocRef);
                            if (recheckDoc.exists()) {
                                console.log('\u2705 Document now exists - onboarding completed successfully');
                                localStorage.removeItem('onboardingOperationId');
                                setUser(currentUser);
                                setIsLoading(false);
                                return;
                            }
                        }
                        
                        // Attempt auto-healing only if still orphaned after recheck
                        const healResult = await healOrphanedUser(currentUser);
                        if (!healResult.success) {
                            logger.error('App', '\u274C Failed to heal orphaned user - user may need to sign up again');
                        }
                    } else {
                        const userData = userDoc.data();
                        
                        // Validate critical fields
                        const missingFields = [];
                        if (!userData.accountEmail) missingFields.push('accountEmail');
                        if (!userData.subscriptionStatus) missingFields.push('subscriptionStatus');
                        if (!userData.trialStartDate) missingFields.push('trialStartDate');
                        
                        if (missingFields.length > 0) {
                            console.warn('\u26A0\uFE0F INCOMPLETE USER DATA:', currentUser.uid);
                            console.warn('   Missing fields:', missingFields);
                            
                            // Attempt to backfill missing fields
                            await backfillMissingFields(currentUser, userData, missingFields);
                        }
                    }
                } catch (error) {
                    logger.error('App', '\u274C Error checking user data integrity:', error);
                }
            }
            
            setUser(currentUser);
            // Don't automatically sign in anonymously - only do this during onboarding
            setIsLoading(false);
        });
        return () => unsubscribe();
    }, []);

    // REMOVED: signInAnonymouslyForTrial() - No longer needed with "Account First" flow
    // REMOVED: transferProgressToNewAccount() - No anonymous users to convert

    const signUpWithEmail = async (email, password, accountName = null) => {
        logger.info('useAuth', 'Info', { 
            email: email,
            accountName: accountName,
            hasAccountName: !!accountName
        });
        trackEvent('signup_attempt', { method: 'email' });

        // Phase 1: Create Firebase Auth account
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const newUser = userCredential.user;
        trackEvent('signup_success', { method: 'email' });
        
        // Track account creation
        trackAccountCreated('email', 'direct');

        // Phase 2: Create Firestore documents atomically with compensating transaction
        const userData = {
            email: newUser.email,
            accountEmail: newUser.email, // Required for nurture email system
            accountName: accountName || newUser.displayName || 'User',
            onboardingComplete: false, // Will be set to true by completeOnboarding()
            profiles: {}, // Will be populated by completeOnboarding()
            lastActiveProfileId: null,
            subscriptionStatus: 'trial',
            trialStartDate: serverTimestamp(),
            trialEndDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
            emailPreferences: {
                promotions: true,
                reports: 'weekly'
            },
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
            emailCampaigns: {
                welcomeSent: false,
                midTrialSent: false,
                trialExpirySent: false
            }
        };
        
        const result = await createUserWithTransaction(newUser, userData, true);
        
        if (!result.success) {
            // If compensating transaction succeeded, user can retry
            if (result.compensated) {
                throw new Error(result.userMessage || 'Account creation failed. Please try again.');
            } else {
                // Orphaned account created - will be cleaned up by cron or healing
                logger.error('useAuth', 'Error');
                throw new Error(result.userMessage || 'Account creation incomplete. Please contact support.');
            }
        }

        logger.info('useAuth', 'Info');
        return userCredential;
    };

    const signInWithEmail = async (email, password) => {
        // Check if we should use mock authentication
        const useRealAuth = localStorage.getItem('speak-roots-use-real-auth') === 'true';
        const useMockAuth = (window.location.hostname === 'localhost' && !useRealAuth) || 
                           localStorage.getItem('speak-roots-use-mock-auth') === 'true';
        
        if (useMockAuth) {
            console.log('Development mode: Simulating email login');
            const mockUser = {
                uid: 'dev-user-123',
                isAnonymous: false,
                email: email,
                displayName: 'Dev User'
            };
            setUser(mockUser);
            return Promise.resolve({ user: mockUser });
        }
        
        trackEvent('login_attempt', { method: 'email' });

        // "Account First" flow - Simple login (no anonymous conversion needed)
        const result = await signInWithEmailAndPassword(auth, email, password);
        trackEvent('login_success', { method: 'email' });
        
        logger.info('useAuth', 'Info');
        return result;
    };

    const socialSignIn = async (provider) => {
        trackEvent('signup_attempt', { method: provider.providerId });

        try {
            // Phase 1: Authenticate with social provider
            const result = await signInWithPopup(auth, provider);
            const isNewUser = getAdditionalUserInfo(result).isNewUser;
            const signedInUser = result.user;

            if (isNewUser) {
                trackEvent('signup_success', { method: provider.providerId });
                trackAccountCreated(provider.providerId, 'social');
                
                // Phase 2: Create Firestore documents atomically with compensating transaction
                const userData = {
                    email: signedInUser.email,
                    accountEmail: signedInUser.email, // Required for nurture email system
                    accountName: signedInUser.displayName || 'User',
                    onboardingComplete: false, // Will be set to true by completeOnboarding()
                    profiles: {}, // Will be populated by completeOnboarding()
                    lastActiveProfileId: null,
                    subscriptionStatus: 'trial',
                    trialStartDate: serverTimestamp(),
                    trialEndDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
                    emailPreferences: {
                        promotions: true,
                        reports: 'weekly'
                    },
                    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
                    emailCampaigns: {
                        welcomeSent: false,
                        midTrialSent: false,
                        trialExpirySent: false
                    }
                };
                
                const txnResult = await createUserWithTransaction(signedInUser, userData, true);
                
                if (!txnResult.success) {
                    // If compensating transaction succeeded, user can retry
                    if (txnResult.compensated) {
                        throw new Error(txnResult.userMessage || 'Account creation failed. Please try again.');
                    } else {
                        // Orphaned account created - will be cleaned up by cron or healing
                        logger.error('useAuth', 'Error');
                        throw new Error(txnResult.userMessage || 'Account creation incomplete. Please contact support.');
                    }
                }

                logger.info('useAuth', 'Info');
            } else {
                trackEvent('login_success', { method: provider.providerId });
                logger.info('useAuth', 'Info');
            }
            
            return result;
        } catch (error) {
            logger.error('App', 'Social sign-in error:', error);
            trackEvent('signup_failed', { method: provider.providerId, error: error.code });
            throw error;
        }
    };

    const doSignOut = () => signOut(auth);

    const resetPassword = (email) => sendPasswordResetEmail(auth, email);

    /**
     * Delete user account with automatic re-authentication handling
     * 
     * Flow:
     * 1. Attempt simple delete first
     * 2. If requires-recent-login error:
     *    - Google users: Trigger Google popup re-auth, then retry delete
     *    - Magic Link users: Throw MAGIC_LINK_REAUTH_REQUIRED (UI will show alert + logout)
     * 3. Cloud Function automatically handles Firestore/Storage cleanup
     */
    const deleteAccount = async () => {
        const user = auth.currentUser;
        
        if (!user) {
            throw new Error("No authenticated account to delete.");
        }

        try {
            // 1. Attempt simple delete first
            // Cloud Function will handle the data cleanup automatically
            await user.delete();
            return { success: true };

        } catch (error) {
            // 2. Catch the specific "Recent Login" error
            if (error.code === 'auth/requires-recent-login') {
                
                // 3. Check which provider they use
                // providerData is an array, usually the first one is the main provider
                const providerId = user.providerData[0]?.providerId;
                
                if (providerId === 'google.com') {
                    // --- GOOGLE FLOW ---
                    try {
                        logger.info('DeleteAccount', 'Info', 'Google re-authentication required');
                        
                        // Trigger the Google popup again to verify identity
                        const provider = new GoogleAuthProvider();
                        await reauthenticateWithPopup(user, provider);
                        
                        // Retry delete after successful re-auth
                        await user.delete();
                        return { success: true };
                        
                    } catch (reAuthError) {
                        logger.error('DeleteAccount', 'Error', 'Google re-auth failed', reAuthError);
                        throw reAuthError;
                    }
                    
                } else {
                    // --- MAGIC LINK FLOW ---
                    // We cannot programmatically re-auth magic links easily.
                    // Throw a specific error that the UI knows how to handle
                    logger.info('DeleteAccount', 'Info', 'Magic Link user requires manual re-auth');
                    throw new Error('MAGIC_LINK_REAUTH_REQUIRED');
                }
                
            } else {
                // Some other error (network, permissions, etc)
                logger.error('DeleteAccount', 'Error', 'Delete account error', error);
                throw error;
            }
        }
    };

    // Development mode functions
    const enableRealAuth = () => {
        if (window.location.hostname === 'localhost') {
            localStorage.setItem('speak-roots-use-real-auth', 'true');
            console.log('Development: Real authentication enabled. Please refresh the page.');
        }
    };

    const enableMockAuth = () => {
        if (window.location.hostname === 'localhost') {
            localStorage.setItem('speak-roots-use-real-auth', 'false');
            console.log('Development: Mock authentication enabled. Please refresh the page.');
        }
    };

    return useMemo(() => ({
        user,
        isLoading,
        // REMOVED: signInAnonymouslyForTrial (no longer needed)
        signUpWithEmail,
        signInWithEmail,
        socialSignIn,
        signOut: doSignOut,
        resetPassword,
        deleteAccount,
        // Development mode functions
        enableRealAuth,
        enableMockAuth
    }), [user, isLoading]);
};

export { useAuth };
